/**
 * Shared utility for parsing SelectionSummary strings from database functions.
 * 
 * Works with both:
 * - f_QuoteSelectionSummary (Quote table - sail-options-select page)
 * - f_Order_QuoteSelectionSummary (Order_Quote table - cart items, orders)
 * 
 * Format: Pipe-delimited (|), ^ prefix for default/standard items
 * Example: "^Full Battens: 2|Reefs: 2 ($79)|Cunningham: Yes ($40)"
 */

export interface SelectionSummaryItem {
    /** Display text (without ^ prefix) */
    text: string;
    /** True if this is a default/standard feature (had ^ prefix) */
    isDefault: boolean;
}

/**
 * Parse a SelectionSummary string into structured items.
 * 
 * @param summary - Pipe-delimited string from f_QuoteSelectionSummary or f_Order_QuoteSelectionSummary
 * @returns Array of parsed items with text and isDefault flag
 * 
 * @example
 * parseSelectionSummary("^Full Battens: 2|Reefs: 2 ($79)")
 * // Returns: [
 * //   { text: "Full Battens: 2", isDefault: true },
 * //   { text: "Reefs: 2 ($79)", isDefault: false }
 * // ]
 */
export const parseSelectionSummary = (summary: string | null | undefined): SelectionSummaryItem[] => {
    if (!summary) return [];
    
    return summary
        .split('|')
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .map(s => ({
            text: s.startsWith('^') ? s.substring(1) : s,
            isDefault: s.startsWith('^')
        }));
};

/**
 * Format SelectionSummary items as a simple string array (text only).
 * Useful for display contexts that don't need default indicators.
 * 
 * @param summary - Pipe-delimited string from database
 * @returns Array of display strings
 */
export const formatSelectionSummaryAsStrings = (summary: string | null | undefined): string[] => {
    return parseSelectionSummary(summary).map(item => item.text);
};
